/**
 * Charts — BarChart (L2). Pure inline SVG (the Flutter twin is CustomPaint):
 * no chart library, no canvas, no external font.
 *
 * CaloryRing used to live here as native L3 code. It is now a DATA
 * composition (`viewir/compositions.js`) of the generic `Gauge` primitive
 * (`components/viz.jsx`) + `Overlay` + `Text` + `Badge` — which is the whole
 * point of the Composed layer: it ships as data and hot-updates, while the
 * pixels it is made of stay generic and baked.
 *
 * The honesty rules live in the components, not in the blueprint:
 *  - a null value is UNKNOWN and renders as "—", never as 0;
 *  - over-target gets its own colour rather than a clipped bar;
 *  - a bar past the end of the window is inert (no tap, no fill).
 *
 * NO DOMAIN VOCABULARY, including in what it SAYS. This chart passed the
 * registry's genericity rule for years while its own source read "Calories per
 * day this week", `unit || 'cal'` and "Previous week" — the contract was
 * generic and the pixels were not, which is the half of the rule a
 * string-only check cannot see. The copy is props now, with neutral defaults:
 *   `unit`         printed after a bar's value in its accessible name ('')
 *   `pageLabel`    the noun ‹ › steps by, for their names ('page')
 *   `windowLabel`  the caption over the series ('' unless the data names one)
 *   `label`        the chart's own accessible name ('Bar chart')
 * A widget that means weeks says so in its blueprint; the kit does not
 * presume it.
 */
import { resetButton, fmt } from './ui';
import { num } from '../paths';

const DAY_MS = 86400000;

/**
 * A bar with a ROUNDED TOP and a flat foot. `rx` on a `<rect>` rounds all four
 * corners, which lifts a bar off its own baseline; a column standing on a chart
 * floor is rounded where it ends, not where it starts. The radius is clamped to
 * half the height so a nearly-empty day draws a cap instead of a crescent.
 */
function barPath(x, y, w, h, radius) {
  const r = Math.max(0, Math.min(radius, w / 2, h));
  return `M${x},${y + h} L${x},${y + r} Q${x},${y} ${x + r},${y} L${x + w - r},${y} Q${x + w},${y} ${x + w},${y + r} L${x + w},${y + h} Z`;
}

/** "2026-08-04" + n days → "2026-08-11". Anything unparseable → null. */
function shiftDay(iso, days) {
  const t = Date.parse(`${String(iso || '').slice(0, 10)}T12:00:00`);
  if (Number.isNaN(t)) return null;
  const d = new Date(t + days * DAY_MS);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** SEALED: the dashed target line + inert future days. Week paging via ‹ ›. */
export function BarChart({ props = {}, data, emit, slots }) {
  const setBars = slots?.setBars;
  const source = setBars?.payload?.bars ?? (props.bars !== undefined ? props.bars : data?.bars);
  const bars = Array.isArray(source) ? source : [];
  const target = props.target != null ? num(props.target) : (data?.target != null ? num(data.target) : null);
  const selected = props.selected ?? data?.anchor ?? null;

  /**
   * Paging moves the ANCHOR, not the bars: the series is derived from a date
   * (`weekBars(args.anchor)`), so a bare `{delta}` has nothing to act on and a
   * page connection wired to it is inert. Resolving the new anchor HERE — one
   * window = one page, however many bars the window holds — is what lets a
   * blueprint wire `pageChanged → page_args.<date arg>` and have the chart,
   * the header and the list move together, exactly like a bar tap does.
   */
  const anchor = selected || data?.anchor || bars[0]?.date || null;
  const nextAnchor = shiftDay(anchor, bars.length || 7);

  /**
   * `maxDate` — THE FUTURE CLAMP. A window is derived from an anchor date, so
   * `›` will happily page into next week and draw seven empty bars for days
   * that cannot hold data. Prod's `›` simply stops. The chart already refuses
   * to let you TAP a future day (`b.future`); this is the same rule applied to
   * the window, and it is a prop rather than a rule because only the blueprint
   * knows whether "today" is the ceiling (a log) or not (a forecast).
   */
  const maxDate = props.maxDate ? String(props.maxDate).slice(0, 10) : null;
  const atEnd = Boolean(maxDate && nextAnchor && nextAnchor > maxDate);

  const page = (delta) => {
    if (delta > 0 && atEnd) return;
    const next = shiftDay(anchor, delta * (bars.length || 7));
    emit?.('pageChanged', next ? { delta, date: next } : { delta });
  };

  // The COPY is the blueprint's, not the kit's. `data.window_label` is the
  // data lane's own answer (a derive that knows what a window means may name
  // it); `week_start` stays readable for the derive that emits it, printed
  // neutrally rather than as a sentence about weeks.
  const pageLabel = props.pageLabel || 'page';
  // `title` is the header heading — default 'This week' (the copy the standalone
  // chart always meant); a composition owning the card passes its own.
  const title = props.title !== undefined ? String(props.title) : 'This week';
  const windowLabel = props.windowLabel
    ?? data?.window_label
    ?? (data?.week_start ? `from ${data.week_start}` : '');
  const unit = props.unit ? ` ${props.unit}` : '';
  // `surface: false` is BARE mode — no card, so a composition (WeekReview) owns
  // the surface. The web chart is bare by nature (a PageShell dashboard panel
  // draws its card), so the prop only ever confirms that; the Flutter twin drops
  // its own `ViewIrSurface`. Default (undeclared) is unchanged on both.

  const W = 320;
  const H = 140;
  const padB = 20;
  // headroom for the SELECTED bar's own value, printed over it — without it a
  // full day's label is clipped by the top of the viewBox
  const padT = 14;
  const plot = H - padB - padT;
  const max = Math.max(target || 0, ...bars.map((b) => num(b.value)), 1) * 1.15;
  const slot = bars.length ? W / bars.length : W;
  const barW = Math.min(26, slot * 0.52);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
      {/* Header: title on the LEFT, the ‹ › pager together on the RIGHT, the two
          VERTICALLY CENTERED (align-items: center). */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
        <span style={{
          flex: 1, minWidth: 0, fontSize: 'var(--t-md)', fontWeight: 700,
          letterSpacing: '-0.15px', color: 'var(--text)', fontFamily: 'var(--font-display)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{title}</span>
        {windowLabel ? (
          <span style={{ fontSize: 'var(--t-xs)', color: 'var(--text-3)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            {windowLabel}
          </span>
        ) : null}
        <button type="button" aria-label={`Previous ${pageLabel}`} onClick={() => page(-1)}
                style={{ ...resetButton, color: 'var(--text-2)', fontSize: 18, padding: '0 4px' }}>‹</button>
        <button type="button" aria-label={`Next ${pageLabel}`} onClick={() => page(1)}
                disabled={atEnd} aria-disabled={atEnd || undefined}
                style={{
                  ...resetButton, fontSize: 18, padding: '0 4px',
                  color: atEnd ? 'var(--text-3)' : 'var(--text-2)',
                  opacity: atEnd ? 0.4 : 1,
                  cursor: atEnd ? 'default' : 'pointer',
                }}>›</button>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img" aria-label={props.label || 'Bar chart'}>
        {target ? (
          <g>
            <line
              x1="0" x2={W}
              y1={H - padB - (plot * target) / max}
              y2={H - padB - (plot * target) / max}
              stroke="var(--line)" strokeWidth="1" strokeDasharray="3 5"
            />
          </g>
        ) : null}
        {bars.map((b, i) => {
          const v = num(b.value);
          const h = max ? (plot * v) / max : 0;
          const x = i * slot + (slot - barW) / 2;
          const y = H - padB - h;
          const isSel = selected != null && b.date === selected;
          // ACCENT is what the eye should land on: the day being read, and any
          // day that got where it was aiming. Everything else is track — a
          // future day (inert) and an ordinary one both sit quiet, and what
          // separates them is the bar's own height, not a third colour.
          const reached = !b.future && target != null && v >= target;
          const lit = !b.future && (isSel || reached);
          const fill = lit ? 'var(--accent)' : 'var(--surface-3)';
          return (
            <g key={b.date}
               // A tappable bar is a control, so it carries a control's
               // accessible name. A future day is inert and gets neither.
               role={b.future ? undefined : 'button'}
               aria-label={b.future ? undefined : `${b.date}: ${v}${unit}`}
               onClick={b.future ? undefined : () => emit?.('barSelected', { date: b.date })}
               style={{ cursor: b.future ? 'default' : 'pointer' }}>
              <rect x={i * slot} y={0} width={slot} height={H} fill="transparent" />
              <path d={barPath(x, Math.min(y, H - padB - 2), barW, Math.max(h, 2), 5)} fill={fill} />
              {isSel && v > 0 ? (
                <text
                  x={i * slot + slot / 2} y={Math.max(y - 5, 9)} textAnchor="middle"
                  fontSize="10" fontWeight="700" fill="var(--accent)"
                >{fmt(v)}</text>
              ) : null}
              <text
                x={i * slot + slot / 2} y={H - 6} textAnchor="middle"
                fontSize="10" fontWeight={isSel ? 700 : 500}
                fill={isSel ? 'var(--accent)' : 'var(--text-3)'}
              >{b.label}</text>
            </g>
          );
        })}
      </svg>
      {/* PER-COLUMN THUMBNAILS. One flex column per bar (flex:1, no h-padding) so
          column i sits under SVG slot i; each column stacks its bar's `thumbs`
          newest-first, circular, at ~the bar's width (52% of the column = the
          same `slot*0.52` ratio as `barW`). A bar with no thumbs is an empty
          column, which keeps every column aligned to its bar. A tap carries the
          tapped row so a wire binds `item.item_id` / `item.session_id` /
          `item.log_card_id`. */}
      {bars.some((b) => Array.isArray(b.thumbs) && b.thumbs.length) ? (
        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
          {bars.map((b) => (
            <div
              key={b.date}
              style={{
                flex: 1, minWidth: 0,
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                gap: 'var(--sp-1)',
              }}
            >
              {(Array.isArray(b.thumbs) ? b.thumbs : []).map((t, i) => (
                <button
                  type="button"
                  key={t.item_id ?? t.log_card_id ?? `${b.date}-${i}`}
                  aria-label={`Open ${b.date}`}
                  onClick={() => emit?.('thumbActivated', { item_id: t.item_id, ...t }, { item: t })}
                  style={{ ...resetButton, width: '52%', cursor: 'pointer' }}
                >
                  <img
                    src={t.image_url} alt=""
                    style={{ width: '100%', aspectRatio: '1', borderRadius: '50%', objectFit: 'cover', display: 'block' }}
                  />
                </button>
              ))}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

